import { get, post, put, del } from '@/lib/api/client'
import {
  PressReleaseFilterParamsSchema,
  PaginationParamsSchema,
  CreatePressReleaseSchema,
  UpdatePressReleaseSchema,
  PressReleaseStatsResponseSchema,
  type PressRelease,
  type PressReleaseDetail,
  type PressReleaseCategory,
  type PressReleaseFilterParams,
  type CreatePressRelease,
  type UpdatePressRelease,
  type PressReleaseStatsResponse,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class PressReleaseService {

  async getPressReleases(
    params: PaginationParams = { page: 1, size: 10 },
    filters: PressReleaseFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = PressReleaseFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<PressRelease>>>(
      `press-release/private/filter?${query}`,
      validatedFilters
    )
  }


  async createPressRelease(data: CreatePressRelease): Promise<ApiResponse<PressReleaseDetail>> {

    const validatedData = CreatePressReleaseSchema.parse(data)


    const payload = {
      title: validatedData.title,
      subtitle: validatedData.subtitle,
      summary: validatedData.summary,
      language: validatedData.language,
      coverImageTitle: validatedData.coverImageTitle,
      additionalImageUrls: validatedData.additionalImageUrls,
      additionalFileUrls: validatedData.additionalFileUrls,
      articleTag: validatedData.articleTag, // Array of strings
      category: validatedData.category,
      readTimeMinutes: validatedData.readTimeMinutes,
      ...(validatedData.landingPage !== undefined && { landingPage: validatedData.landingPage }), // Now managed via WebPages
      pressRelease: validatedData.pressRelease,
      pressReleaseBanner: validatedData.pressReleaseBanner,
      allowComment: validatedData.allowComment,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl // Image URL string - API expects this field name
    }

    return await post<ApiResponse<PressReleaseDetail>>(
      'press-release/admin/private',
      payload
    )
  }


  async updatePressRelease(
    pressReleaseId: string,
    data: UpdatePressRelease
  ): Promise<ApiResponse<null>> {
    // Validate data
    const validatedData = UpdatePressReleaseSchema.parse(data)

    // Use JSON body instead of FormData since we're sending URLs
    const payload = {
      title: validatedData.title,
      subtitle: validatedData.subtitle,
      summary: validatedData.summary,
      language: validatedData.language,
      coverImageTitle: validatedData.coverImageTitle,
      additionalImageUrls: validatedData.additionalImageUrls,
      additionalFileUrls: validatedData.additionalFileUrls,
      articleTag: validatedData.articleTag, // Array of strings
      category: validatedData.category,
      readTimeMinutes: validatedData.readTimeMinutes,
      ...(validatedData.landingPage !== undefined && { landingPage: validatedData.landingPage }), // Now managed via WebPages
      pressRelease: validatedData.pressRelease,
      pressReleaseBanner: validatedData.pressReleaseBanner,
      allowComment: validatedData.allowComment,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl
    }

    return await put<ApiResponse<null>>(
      `press-release/admin/private/${pressReleaseId}`,
      payload
    )
  }


  async updatePressReleaseStatus(pressReleaseId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<PressRelease>> {
    return await put<ApiResponse<PressRelease>>(
      `press-release/admin/private/${pressReleaseId}/status`,
      { status }
    )
  }


  async deletePressRelease(pressReleaseId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `press-release/admin/private/${pressReleaseId}`
    )
  }


  async getAllPressReleases(): Promise<ApiResponse<PressRelease[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getPressReleaseById(pressReleaseId: string): Promise<ApiResponse<PressReleaseDetail>> {
    return await get<ApiResponse<PressReleaseDetail>>(
      `press-release/private/${pressReleaseId}`
    )
  }


  async searchPressReleases(
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const queryParams = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    })

    // Add searchBy if provided (can be empty string)
    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    // Use public endpoint as per API documentation
    return await get<ApiResponse<PaginatedResponse<PressRelease>>>(
      `press-release/private/search?${queryParams.toString()}`
    )
  }


  async getTotalPressReleases(): Promise<ApiResponse<{ count: number }>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getPressReleaseStats(): Promise<PressReleaseStatsResponse> {
    const res = await get('press-release/private/stats')
    return PressReleaseStatsResponseSchema.parse(res)
  }


  async getTotalPublishedPressReleases(): Promise<ApiResponse<{ publishedPressReleases: number }>> {
    return await get<ApiResponse<{ publishedPressReleases: number }>>(
      'press-release/private/stats/published'
    )
  }


  async getTotalDraftPressReleases(): Promise<ApiResponse<{ draftPressReleases: number }>> {
    return await get<ApiResponse<{ draftPressReleases: number }>>(
      'press-release/private/stats/draft'
    )
  }


  async getArticleTags(): Promise<ApiResponse<string[]>> {
    return await get<ApiResponse<string[]>>(
      'press-release/private/article-tags'
    )
  }

  async getPressReleaseCategories(): Promise<ApiResponse<PressReleaseCategory[]>> {
    return await get<ApiResponse<PressReleaseCategory[]>>(
      'press-release/private/categories'
    )
  }


  async getPublicPressReleases(): Promise<ApiResponse<PressRelease[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }

  /**
   * Get home page highlights
   * GET /press-release/public/highlights?type={type}
   */
  async getHomePageHighlights(type: string = 'HOME_PAGE_HERO'): Promise<ApiResponse<PressRelease[]>> {
    return await get<ApiResponse<PressRelease[]>>(
      `press-release/public/highlights?type=${type}`
    )
  }

  /**
   * Update home page highlights
   * POST /press-release/private/highlight
   */
  async updateHomePageHighlights(
    pressReleaseIds: string[],
    type: string = 'HOME_PAGE_HERO',
    landingPagePositions: Record<string, number> = {}
  ): Promise<ApiResponse<null>> {
    return await post<ApiResponse<null>>('press-release/private/highlight', {
      pressReleaseIds,
      pressReleaseHighlightType: type,
      landingPagePositions
    })
  }
}


export const pressReleaseService = new PressReleaseService()

// Export default
export default pressReleaseService
